import { Box, Grid, InputAdornment, TableHead, TableRow, TableCell, TableBody, Skeleton, Typography, Table, Card, CardContent, TableFooter, Tooltip } from "@mui/material";
import React, { useMemo, useEffect, useCallback } from "react";
import { useGetRangeSettingGroupBySite, useGetSieveSets, useGetSieveSetById } from "../../../queries/QualityControlQueries";
import { EntitySelect } from "../../general/EntitySelect";
import { isValidNumber, useSieveAnalysisCalculationHooks } from "./Calculations";
import { SampleSieveRangesTable } from "./SampleSieveRangesTable";
import { Sample, SieveSetAnalysis, SieveSet, Sieve, SieveAnalysis } from "../../../types/QualityControlModels";
import { SelectOption } from "../../../utilities/SelectUtilities";
import { handleInputFocus } from "../../general/UxUtilities";
import { isEqual } from "lodash";
import { Warning } from "@mui/icons-material";
import { VividTextField } from "./styled";
import { useCurrentSite } from "../../../hooks/general/useCurrentSite";

export const SampleSieveSetAnalysis = ({
    sampleUpdate,
    isCoreSample,
    setSampleUpdate,
    showTitle = true,
    isReadOnly,
    disabled,
}: {
    sampleUpdate: Sample;
    isCoreSample: boolean;
    setSampleUpdate: (value: any) => void;
    showTitle: boolean;
    isReadOnly: boolean;
    disabled: boolean;
}) => {
    const {
        calculateWeightLostInWash,
        calculatePercentLostInWash,
        calculateMoistureRate,
        calculateOversizePercent,
        calculateOrePercent,
        calculateSieveFinalWeight,
        calculateSievePercentFinal,
        calculateTotalWeight,
        calculateSieveRange,
    } = useSieveAnalysisCalculationHooks();
    const { site } = useCurrentSite();
    const sieveSetAnalysis = sampleUpdate?.sieveSetAnalysis || ({} as SieveSetAnalysis);
    const sieveAnalyses = sieveSetAnalysis?.sieveAnalyses ?? [];
    const updateSieveSetAnalysis = useCallback(
        (updater: Partial<SieveSetAnalysis> | ((prev: SieveSetAnalysis) => SieveSetAnalysis)) => {
            setSampleUpdate((prevState: Sample) => {
                if (!prevState) {
                    return prevState;
                }
                const prevSieveSetAnalysis = prevState.sieveSetAnalysis || ({} as SieveSetAnalysis);
                const nextSieveSetAnalysis =
                    typeof updater === "function"
                        ? updater(prevSieveSetAnalysis)
                        : {
                            ...prevSieveSetAnalysis,
                            ...updater,
                        };
                return {
                    ...prevState,
                    sieveSetAnalysis: nextSieveSetAnalysis,
                };
            });
        },
        [setSampleUpdate]
    );

    const { data: sieveSets, isLoading: isLoadingSieveSets }: { data: SieveSet[]; isLoading: boolean } = useGetSieveSets();
    const { data: sieveGroup, isLoading: isLoadingRangeSettingGroup } = useGetRangeSettingGroupBySite(site?.id);

    const { data: sieveSet } = useGetSieveSetById({
        sieveSetId: sieveSetAnalysis?.sieveSetId,
        options: {
            enabled: !!sieveSetAnalysis?.sieveSetId,
        },
    });

    const oversizePercent = useMemo(() => {
        const oversizeWeight = sieveSetAnalysis?.oversizeWeight || 0;
        const preWashDryWeight = sieveSetAnalysis?.preWashDryWeight || 0;

        if (oversizeWeight && preWashDryWeight) {
            return calculateOversizePercent({
                oversizeWeight,
                preWashDryWeight,
            });
        }
    }, [calculateOversizePercent, sieveSetAnalysis?.oversizeWeight, sieveSetAnalysis?.preWashDryWeight]);

    const percentLostInWash = useMemo(() => {
        const preWashDryWeight = sieveSetAnalysis?.preWashDryWeight;
        const postWashDryWeight = sieveSetAnalysis?.postWashDryWeight;

        if (postWashDryWeight && preWashDryWeight) {
            return calculatePercentLostInWash({
                preWashDryWeight,
                postWashDryWeight,
                oversizePercent,
            });
        }
    }, [calculatePercentLostInWash, oversizePercent, sieveSetAnalysis?.postWashDryWeight, sieveSetAnalysis?.preWashDryWeight]);

    const weightLostInWash = useMemo(() => {
        const preWashDryWeight = sieveSetAnalysis?.preWashDryWeight;
        const postWashDryWeight = sieveSetAnalysis?.postWashDryWeight;
        const oversizeWeight = sieveSetAnalysis?.oversizeWeight ?? 0;
        if (postWashDryWeight && preWashDryWeight) {
            return calculateWeightLostInWash({
                preWashDryWeight,
                postWashDryWeight,
                oversizeWeight,
            });
        }
    }, [calculateWeightLostInWash, sieveSetAnalysis?.oversizeWeight, sieveSetAnalysis?.postWashDryWeight, sieveSetAnalysis?.preWashDryWeight]);

    const moistureRate = useMemo(() => {
        const preWashDryWeight = sieveSetAnalysis?.preWashDryWeight;
        const beginningWetWeight = sieveSetAnalysis?.beginningWetWeight;

        if (preWashDryWeight && beginningWetWeight) {
            return calculateMoistureRate({
                beginningWetWeight,
                preWashDryWeight,
            });
        }
    }, [calculateMoistureRate, sieveSetAnalysis?.beginningWetWeight, sieveSetAnalysis?.preWashDryWeight]);

    const orePercent = useMemo(() => {
        const preWashDryWeight = sieveSetAnalysis?.preWashDryWeight;
        const postWashDryWeight = sieveSetAnalysis?.postWashDryWeight;

        return calculateOrePercent({
            preWashDryWeight,
            postWashDryWeight,
        });
    }, [calculateOrePercent, sieveSetAnalysis?.postWashDryWeight, sieveSetAnalysis?.preWashDryWeight]);

    const handleOversizeWeightChange = useCallback((event) => {
        const oversizeWeight = event.target.value;
        updateSieveSetAnalysis((prevState) => ({ ...prevState, oversizeWeight: parseFloat(oversizeWeight || 0) }));
    }, [updateSieveSetAnalysis]);

    const handleWetWeightChange = useCallback((event) => {
        const beginningWetWeight = event.target.value.trim();
        updateSieveSetAnalysis((prevState) => ({
            ...prevState,
            beginningWetWeight: parseFloat(beginningWetWeight || 0),
        }));
    }, [updateSieveSetAnalysis]);

    const handlePreWashDryWeightChange = useCallback((event) => {
        const preWashDryWeight = event.target.value.trim();

        updateSieveSetAnalysis((prevState) => ({
            ...prevState,
            preWashDryWeight: parseFloat(preWashDryWeight || 0),
        }));
    }, [updateSieveSetAnalysis]);

    const handlePostWashDryWeightChange = useCallback((event) => {
        const postWashDryWeight = event.target.value.trim();
        updateSieveSetAnalysis((prevState) => ({
            ...prevState,
            postWashDryWeight: parseFloat(postWashDryWeight || 0),
        }));
    }, [updateSieveSetAnalysis]);

    const handleSplitSampleWeightChange = useCallback((event) => {
        const splitSampleWeight = event.target.value.trim();

        if (isValidNumber(splitSampleWeight)) {
            updateSieveSetAnalysis((prevState) => ({
                ...prevState,
                splitSampleWeight: splitSampleWeight === "" ? null : parseFloat(splitSampleWeight),
            }));
        }
    }, [updateSieveSetAnalysis]);

    const handleSelectSieveSet = useCallback((option: SelectOption) => {
        const sieveSetId = option?.value ? Number(option.value) : null;
        updateSieveSetAnalysis((prevState) => ({
            ...prevState,
            sieveSetId,
            sieveAnalyses: [],
            ranges: [],
            totalWeight: 0,
        }));
    }, [updateSieveSetAnalysis]);

    const updateSieveAnalysis = useCallback(
        (sieveId: number | undefined, changes: Partial<SieveAnalysis>) => {
            if (!sieveId) {
                return;
            }
            updateSieveSetAnalysis((prevState: SieveSetAnalysis) => ({
                ...prevState,
                sieveAnalyses: (prevState.sieveAnalyses ?? []).map((analysis) => (analysis.sieveId === sieveId ? { ...analysis, ...changes } : analysis)),
            }));
        },
        [updateSieveSetAnalysis]
    );

    const activeSieveSets = useMemo(() => sieveSets?.filter((sieveSet) => sieveSet.isActive), [sieveSets]);
    const rangeSettings = useMemo(
        () => sieveGroup?.rangeSettingGroup?.rangeSettingGroupRangeSettings?.map(({ rangeSetting }) => rangeSetting) ?? [],
        [sieveGroup?.rangeSettingGroup?.rangeSettingGroupRangeSettings]
    );
    const { analyses: calculatedAnalyses, totalWeight: calculatedTotalWeight } = useMemo(() => {
        if (!sieveAnalyses?.length) {
            return { analyses: [], totalWeight: 0 };
        }

        const analysesWithFinalWeight = sieveAnalyses.map((analysis) => {
            let finalWeight = analysis.finalWeight;
            if (!sieveSet?.isCamsizer) {
                finalWeight = calculateSieveFinalWeight({
                    startWeight: analysis.startWeight,
                    endWeight: analysis.endWeight,
                });
            }
            return {
                ...analysis,
                finalWeight,
            };
        });

        const totalWeight = calculateTotalWeight({ analyses: analysesWithFinalWeight }) || 0;
        const analysesWithPercentFinal = analysesWithFinalWeight.map((analysis) => ({
            ...analysis,
            percentFinal: calculateSievePercentFinal({
                sieveFinalWeight: analysis.finalWeight,
                sievesTotalWeight: totalWeight,
                orePercent,
                isPan: analysis.sieve?.isPan,
                percentLostInWash,
            }),
        }));

        return { analyses: analysesWithPercentFinal, totalWeight };
    }, [
        sieveAnalyses,
        sieveSet?.isCamsizer,
        calculateSieveFinalWeight,
        calculateTotalWeight,
        calculateSievePercentFinal,
        orePercent,
        percentLostInWash,
    ]);
    const isRangesReady = useMemo(
        () => calculatedAnalyses.length > 0 && !isLoadingRangeSettingGroup && rangeSettings.length > 0,
        [calculatedAnalyses.length, isLoadingRangeSettingGroup, rangeSettings.length]
    );
    const ranges = useMemo(() => {
        if (!isRangesReady) {
            return null;
        }

        return rangeSettings.map((rangeSetting) => {
            const isOversize = Number(rangeSetting.endSieve) === 10;
            const rangeValue = isOversize
                ? Number(oversizePercent ?? 0)
                : calculateSieveRange({
                    analyses: calculatedAnalyses,
                    rangeSetting,
                });
            const normalizedValue = typeof rangeValue === "string" ? "" : rangeValue ?? "";

            return {
                rangeSettingId: rangeSetting.id,
                rangeSetting,
                value: normalizedValue,
            };
        });
    }, [calculateSieveRange, calculatedAnalyses, isRangesReady, oversizePercent, rangeSettings]);

    useEffect(() => {
        if (!sieveSetAnalysis?.sieveAnalyses?.length && sieveSet?.sieves?.length > 0) {
            updateSieveSetAnalysis((prevState: SieveSetAnalysis) => ({
                ...prevState,
                sieveAnalyses: sieveSet.sieves.map(
                    (sieve: Sieve): SieveAnalysis => ({
                        sieveId: sieve.id,
                        startWeight: sieve?.currentWeight || sieve?.initialWeight || 0,
                        sieve,
                        endWeight: 0,
                        finalWeight: 0,
                        percentFinal: 0,
                    })
                ),
            }));
        }
    }, [sieveSet, sieveSetAnalysis?.sieveAnalyses?.length, updateSieveSetAnalysis]);

    useEffect(() => {
        if (!sampleUpdate) {
            return;
        }

        let shouldUpdate = false;
        const nextSieveSetAnalysis = { ...sieveSetAnalysis };

        if (!isEqual(sieveSetAnalysis?.sieveAnalyses, calculatedAnalyses)) {
            nextSieveSetAnalysis.sieveAnalyses = calculatedAnalyses;
            shouldUpdate = true;
        }

        if (!isEqual(sieveSetAnalysis?.totalWeight, calculatedTotalWeight)) {
            nextSieveSetAnalysis.totalWeight = calculatedTotalWeight;
            shouldUpdate = true;
        }

        if (!isEqual(sieveSetAnalysis?.moistureRate, moistureRate)) {
            nextSieveSetAnalysis.moistureRate = moistureRate;
            shouldUpdate = true;
        }

        if (ranges && !isEqual(sieveSetAnalysis?.ranges, ranges)) {
            nextSieveSetAnalysis.ranges = ranges;
            shouldUpdate = true;
        }

        if (shouldUpdate) {
            updateSieveSetAnalysis(nextSieveSetAnalysis);
        }
    }, [calculatedAnalyses, calculatedTotalWeight, moistureRate, ranges, sampleUpdate, sieveSetAnalysis, updateSieveSetAnalysis]);

    return (
        <Card elevation={0}>
            <CardContent>
                <Grid container spacing={2}>
                    {showTitle && (
                        <Grid item xs={12}>
                            <Typography variant="h6">Sieve Analysis</Typography>
                        </Grid>
                    )}
                    <Grid item xs={3}>
                        <EntitySelect
                            label="Sieve Set"
                            entities={activeSieveSets}
                            isLoadingEntities={isLoadingSieveSets}
                            selectedValues={sieveSetAnalysis?.sieveSetId}
                            handleChange={handleSelectSieveSet}
                            filter={(sieveSet) => sieveSet.sites.some((site) => site.id === sampleUpdate?.location?.plant?.siteId)}
                            disabled={isReadOnly || disabled}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Raw Sample Wet Weight"
                            type="number"
                            onFocus={handleInputFocus}
                            onChange={handleWetWeightChange}
                            value={sieveSetAnalysis?.beginningWetWeight || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            size="small"
                            disabled={isReadOnly || disabled}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Raw Sample Dry Weight"
                            type="number"
                            onFocus={handleInputFocus}
                            onChange={handlePreWashDryWeightChange}
                            value={sieveSetAnalysis?.preWashDryWeight || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            size="small"
                            disabled={isReadOnly || disabled}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Washed Sand Dry Weight"
                            type="number"
                            onFocus={handleInputFocus}
                            onChange={handlePostWashDryWeightChange}
                            value={sieveSetAnalysis?.postWashDryWeight || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled={isReadOnly || disabled}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Washed Oversize Dry Weight"
                            type="number"
                            onFocus={handleInputFocus}
                            onChange={handleOversizeWeightChange}
                            value={sieveSetAnalysis?.oversizeWeight || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled={isReadOnly || disabled}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Washed Oversize"
                            type="number"
                            onFocus={handleInputFocus}
                            value={oversizePercent || 0}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                inputProps: { step: 0.01 },
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                            }}
                            disabled
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Split Sample Weight"
                            type="number"
                            onFocus={handleInputFocus}
                            onChange={handleSplitSampleWeightChange}
                            value={sieveSetAnalysis?.splitSampleWeight || ""}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled={isReadOnly || disabled}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Raw Sample Moisture"
                            type="number"
                            onFocus={handleInputFocus}
                            value={moistureRate || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            disabled
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Washed Loss"
                            type="number"
                            onFocus={handleInputFocus}
                            value={weightLostInWash || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VividTextField
                            label="Washed Loss"
                            type="number"
                            onFocus={handleInputFocus}
                            value={percentLostInWash || 0}
                            InputProps={{
                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            disabled
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    {isCoreSample && (
                        <Grid item xs={3}>
                            <VividTextField
                                label="Ore Percent"
                                onFocus={handleInputFocus}
                                InputProps={{
                                    onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                value={orePercent || ""}
                                fullWidth
                                disabled
                                size="small"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {sieveSetAnalysis?.sieveSetId ? (
                            !!sieveSet?.sieves && (
                                <Box>
                                    <SieveAnalysesTable
                                        sieveSet={sieveSet}
                                        sieveAnalyses={calculatedAnalyses}
                                        splitSampleWeight={sieveSetAnalysis?.splitSampleWeight}
                                        totalWeight={calculatedTotalWeight}
                                        onUpdateAnalysis={updateSieveAnalysis}
                                        isReadOnly={isReadOnly}
                                        disabled={disabled}
                                    />
                                    {sieveSetAnalysis?.sieveAnalyses && !!sieveSetAnalysis?.sieveAnalyses.length && (
                                        <SampleSieveRangesTable ranges={ranges} rangeSettings={rangeSettings} isReady={isRangesReady} />
                                    )}
                                </Box>
                            )
                        ) : (
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        Select a Sieve Set to Continue
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const SieveAnalysesTable = ({
    sieveSet,
    sieveAnalyses,
    splitSampleWeight,
    totalWeight,
    onUpdateAnalysis,
    isReadOnly,
    disabled,
}: {
    sieveSet: SieveSet;
    sieveAnalyses: SieveAnalysis[];
    splitSampleWeight: number | "" | null | undefined;
    totalWeight: number;
    onUpdateAnalysis: (sieveId: number | undefined, changes: Partial<SieveAnalysis>) => void;
    isReadOnly: boolean;
    disabled: boolean;
}) => {
    const { calculateSplitSampleDelta } = useSieveAnalysisCalculationHooks();
    const sortedAnalyses = useMemo(() => [...sieveAnalyses].sort((a, b) => a.sieve?.displayOrder - b.sieve?.displayOrder), [sieveAnalyses]);
    const sievesTotalWeight = totalWeight ?? 0;

    const handleStartWeightChange = useCallback(
        ({ event, sieveId }) => {
            const startWeight = parseFloat(event.target.value);

            if (isValidNumber(startWeight)) {
                onUpdateAnalysis(sieveId, { startWeight });
            }
        },
        [onUpdateAnalysis]
    );

    const handleEndWeightChange = useCallback(
        ({ event, sieveId }) => {
            const endWeight = parseFloat(event.target.value);

            if (isValidNumber(endWeight)) {
                onUpdateAnalysis(sieveId, { endWeight });
            }
        },
        [onUpdateAnalysis]
    );

    /**
     *  For Camsizer Only
     */
    const handleFinalWeightChange = useCallback(
        ({ event, sieveId }) => {
            const finalWeight = parseFloat(event.target.value);

            if (isValidNumber(finalWeight)) {
                onUpdateAnalysis(sieveId, { finalWeight });
            }
        },
        [onUpdateAnalysis]
    );

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Screen Size</TableCell>
                    {!sieveSet?.isCamsizer && (
                        <>
                            <TableCell>Start Weight</TableCell>
                            <TableCell>End Weight</TableCell>
                        </>
                    )}
                    <TableCell>{sieveSet?.isCamsizer ? "Camsizer" : "Final Weight"}</TableCell>
                    <TableCell>Percent Final</TableCell>
                    {/* <TableCell>30 Day Avg</TableCell>
                    <TableCell>30 Day Std</TableCell> */}
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedAnalyses.map((sieveAnalysis: SieveAnalysis, index: number) => {
                    const sieveId = sieveAnalysis.sieveId ?? sieveAnalysis.sieve?.id;
                    return (
                        <TableRow key={sieveId ?? index}>
                            <TableCell>{sieveAnalysis?.sieve?.size}</TableCell>

                            {!sieveSet?.isCamsizer ? (
                                <>
                                    <TableCell>
                                        <VividTextField
                                            required
                                            type="number"
                                            value={sieveAnalysis?.startWeight || 0}
                                            InputProps={{
                                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                            }}
                                            onFocus={handleInputFocus}
                                            onChange={(event) =>
                                                handleStartWeightChange({
                                                    sieveId,
                                                    event,
                                                })
                                            }
                                            disabled={isReadOnly || disabled}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <VividTextField
                                            required
                                            type="number"
                                            onScroll={() => false}
                                            value={sieveAnalysis?.endWeight || 0}
                                            InputProps={{
                                                onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                            }}
                                            onFocus={handleInputFocus}
                                            onChange={(event) =>
                                                handleEndWeightChange({
                                                    sieveId,
                                                    event,
                                                })
                                            }
                                            disabled={isReadOnly || disabled}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                </>
                            ) : (
                                <></>
                            )}

                            <TableCell>
                                <VividTextField
                                    value={sieveAnalysis?.finalWeight}
                                    onFocus={handleInputFocus}
                                    onChange={(event) =>
                                        handleFinalWeightChange({
                                            sieveId,
                                            event,
                                        })
                                    }
                                    InputProps={{
                                        onWheel: (event) => (event.target as HTMLInputElement).blur(),
                                        endAdornment: (
                                            <>
                                                {Number(sieveAnalysis?.finalWeight) < 0 && (
                                                    <Tooltip title="Final weight cannot be negative" arrow>
                                                        <InputAdornment position="start">
                                                            <Warning color="error" />
                                                        </InputAdornment>
                                                    </Tooltip>
                                                )}
                                                <InputAdornment position="end">g</InputAdornment>
                                            </>
                                        ),
                                    }}
                                    disabled={!sieveSet?.isCamsizer || disabled || isReadOnly}
                                    size="small"
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <VividTextField
                                    value={isFinite(Number(sieveAnalysis.percentFinal)) ? sieveAnalysis.percentFinal : 0}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    size="small"
                                    fullWidth
                                    disabled
                                />
                            </TableCell>
                            {/* <TableCell></TableCell>
                            <TableCell></TableCell> */}
                        </TableRow>
                    );
                })}
            </TableBody>
            <TableFooter>
                <TableRow>
                    {!sieveSet?.isCamsizer ? (
                        <>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>Total</TableCell>
                        </>
                    ) : (
                        <TableCell>Total</TableCell>
                    )}
                    <TableCell>
                        <VividTextField
                            value={sievesTotalWeight}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled
                            size="small"
                            fullWidth
                        />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                </TableRow>
                <TableRow>
                    {!sieveSet?.isCamsizer ? (
                        <>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </>
                    ) : (
                        <></>
                    )}

                    <TableCell>Split Sample</TableCell>
                    <TableCell>
                        <VividTextField
                            value={splitSampleWeight || ""}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled
                            size="small"
                            fullWidth
                        />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                </TableRow>
                <TableRow>
                    {!sieveSet?.isCamsizer ? (
                        <>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </>
                    ) : null}

                    <TableCell>Detla Δ</TableCell>
                    <TableCell>
                        <VividTextField
                            value={calculateSplitSampleDelta({
                                totalWeight: sievesTotalWeight,
                                splitSampleWeight,
                            })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">g</InputAdornment>,
                            }}
                            disabled
                            size="small"
                            fullWidth
                        />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    );
};

export const SampleSieveAnalysisSkeleton = () => (
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <Typography variant="h6">
                <Skeleton />
            </Typography>
        </Grid>
        <Grid item xs={12}>
            <Grid container spacing={2}>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={3}>
                    <Skeleton />
                </Grid>
                <Grid item xs={12}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                                <TableCell>
                                    <Skeleton />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>
        </Grid>
    </Grid>
);
